import { useEffect, useState } from 'react';

/** Font specs aligned with @fontsource imports in src/index.tsx and icon @font-face in src/index.css */
const CRITICAL_FONT_SPECS = [
  '24px "Material Symbols Rounded"',
  '400 16px Roboto',
  'italic 400 16px Roboto',
  '500 16px Roboto',
  '700 16px Roboto',
  '500 16px "Roboto Mono"',
] as const;

const FONT_LOAD_TIMEOUT_MS = 3000;

function areCriticalFontsReady(): boolean {
  if (typeof document === 'undefined' || !('fonts' in document)) {
    return true;
  }
  try {
    return CRITICAL_FONT_SPECS.every((spec) => document.fonts.check(spec));
  } catch {
    return true;
  }
}

function loadCriticalFonts(): Promise<void> {
  return Promise.all(CRITICAL_FONT_SPECS.map((spec) => document.fonts.load(spec))).then(
    () => undefined
  );
}

/**
 * Returns true when critical app fonts (Material Symbols, Roboto, Roboto Mono) have
 * been loaded by the browser and are ready for rendering.
 *
 * Provides safe fallback for environments without CSS Font Loading API (SSR/jsdom)
 * or when network stalls (via a timeout fallback).
 */
export function useCriticalFontsReady(): boolean {
  const [isReady, setIsReady] = useState<boolean>(areCriticalFontsReady);

  useEffect(() => {
    if (isReady) return;

    if (
      typeof document === 'undefined' ||
      !('fonts' in document) ||
      typeof document.fonts.load !== 'function'
    ) {
      setIsReady(true);
      return;
    }

    let isMounted = true;
    const timeout = setTimeout(() => {
      if (isMounted) setIsReady(true);
    }, FONT_LOAD_TIMEOUT_MS);

    loadCriticalFonts()
      .then(() => {
        if (isMounted) setIsReady(true);
      })
      .catch(() => {
        if (isMounted) setIsReady(true);
      })
      .finally(() => {
        clearTimeout(timeout);
      });

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, [isReady]);

  return isReady;
}

export default useCriticalFontsReady;
